import { Component } from '@theme/component';

/**
 * Handles collection switching functionality
 *
 * @typedef {Object} CollectionSwitcherRefs
 * @property {HTMLButtonElement[]} buttons - The collection switcher buttons
 *
 * @extends {Component<CollectionSwitcherRefs>}
 */
class CollectionSwitcherComponent extends Component {
  requiredRefs = ['buttons'];

  connectedCallback() {
    super.connectedCallback();
    this.#observeOverflowCount();
  }

  /**
   * Observe overflow-list count changes and update the more button text
   */
  #observeOverflowCount() {
    const overflowList = this.querySelector('overflow-list');
    if (!overflowList) return;

    const updateCount = () => {
      const count = getComputedStyle(overflowList).getPropertyValue('--overflow-count').trim();
      const countEl = this.querySelector('.collection-switcher__more-count');
      if (countEl) countEl.textContent = count || '0';
    };

    const observer = new MutationObserver(updateCount);
    observer.observe(overflowList, { attributes: true, attributeFilter: ['style'] });

    // Initial update after overflow-list initializes
    requestAnimationFrame(() => setTimeout(updateCount, 100));
  }

  /**
   * Shows all collection items in the overflow list
   */
  showAllCollections() {
    const overflowList = this.querySelector('overflow-list');
    if (overflowList) overflowList.showAll();
  }

  /**
   * Switches to the specified collection
   * @param {Object} data - The data object
   * @param {string} data.collectionId - The collection ID to switch to
   */
  switchCollection({ collectionId }) {
    const section = this.closest('.shopify-section');
    if (!section) return;

    // Find all collection switcher panels in this section
    const collectionContainers = section.querySelectorAll('.collection-switcher-panel');

    // Hide all collections, show the selected one
    for (const container of collectionContainers) {
      if (container instanceof HTMLElement) {
        const isSelected = container.dataset.collectionId === String(collectionId);
        container.hidden = !isSelected;
      }
    }

    // Update button active states in ALL collection-switcher components in this section
    const allSwitchers = section.querySelectorAll('collection-switcher-component');

    for (const switcher of allSwitchers) {
      const buttons = switcher.querySelectorAll('.collection-switcher__button');

      for (const button of buttons) {
        if (button instanceof HTMLElement) {
          const isActive = button.dataset.collectionId === String(collectionId);
          button.classList.toggle('collection-switcher__button--active', isActive);
          button.setAttribute('aria-current', isActive.toString());
        }
      }
    }
  }
}

if (!customElements.get('collection-switcher-component')) {
  customElements.define('collection-switcher-component', CollectionSwitcherComponent);
}
